import React from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { DesktopKpiStrip } from "../shared/DesktopKpiStrip";
import { DeskEmpty, DeskPanel, DeskPill, DeskTable } from "../desktop/DeskTable";
import { CPDropdown } from "../CPDropdown";
import { CPInput } from "../CPInput";
import { formatHoursMinutes } from "../../helpers/timeHelpers";
import { formatSessionShiftLabel } from "../../helpers/dateTimeHelpers";
import "./intelDesktop.css";

const TONE_BY_ACCENT = {
  "intel-card-gold": "gold",
  "intel-card-green": "green",
  "intel-card-red": "red",
  "intel-card-blue": "blue",
  "intel-card-purple": "purple",
};

const cash = (amount) => `$${Math.round(Number(amount || 0)).toLocaleString("en-CA")}`;
const day = (iso, opts = { month: "short", day: "numeric", year: "numeric" }) => {
  if (!iso) return "—";
  const date = new Date(`${iso}T12:00:00`);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString("en-US", opts);
};

/** Report Center (desktop): card grid of report modules. "Soon" modules stay disabled. */
export function IntelligenceHubDesktop({ cards, roleLabel }) {
  return (
    <CrewPilotModuleShell
      title="Report Center"
      subtitle={roleLabel ? `Report modules · ${roleLabel}` : "Report modules"}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
    >
      <div className="rpt-grid" aria-label="Report modules">
        {cards.map((card) => (
          <button
            key={card.id}
            type="button"
            className={`rpt-card rpt-card--${TONE_BY_ACCENT[card.accentClass] || "gold"}`}
            onClick={card.active ? card.onClick : undefined}
            disabled={!card.active}
            aria-label={card.title}
          >
            <span className="rpt-card__icon">{card.icon}</span>
            <span>
              <h2 className="rpt-card__title">{card.title}</h2>
              <p className="rpt-card__desc">{card.description}</p>
            </span>
            <span className="rpt-card__foot">
              {card.active ? (
                <span className="rpt-card__open">Open →</span>
              ) : (
                <DeskPill tone="muted">Soon</DeskPill>
              )}
            </span>
          </button>
        ))}
      </div>
    </CrewPilotModuleShell>
  );
}

function DateField({ label, value, onChange }) {
  return (
    <div>
      <CPInput label={label} type="date" value={value} onChange={onChange} />
      {value ? <p className="rpt-config__hint">{day(value, { month: "long", day: "numeric", year: "numeric" })}</p> : null}
    </div>
  );
}

/** Hours reports (desktop): configure on the left, result on the right. */
export function ReportBuilderDesktop({
  onBack,
  reportScope,
  onScope,
  scopeOptions,
  selectedEmployee,
  onEmployee,
  employees,
  reportType,
  onReportType,
  project,
  onProject,
  projects,
  startDate,
  endDate,
  onStartDate,
  onEndDate,
  canGenerate,
  onGenerate,
  isGenerating,
  reportResult,
}) {
  const isProject = reportType === "hours_by_project";
  const isRange = reportType === "hours_by_date_range";

  const logColumns = [
    { key: "date", label: "Date", width: "120px", render: (l) => <span className="dt-nowrap">{day(l.date_iso)}</span> },
    { key: "emp", label: "Employee", width: "16%", render: (l) => <span className="dt-strong">{l.employee || "N/A"}</span> },
    {
      key: "task",
      label: "Task",
      render: (l) => (
        <>
          <span className="dt-strong">{l.work_completed || "N/A"}</span>
          <span className="dt-sub">{l.work_category || "N/A"}{l.project_name ? ` · ${l.project_name}` : ""}</span>
        </>
      ),
    },
    {
      key: "shift",
      label: "Shift",
      width: "160px",
      render: (l) => <span className="dt-muted dt-nowrap">{l.start_time && l.end_time ? formatSessionShiftLabel(l.start_time, l.end_time) : "N/A"}</span>,
    },
    { key: "logged", label: "Logged", align: "right", width: "86px", render: (l) => <span className="dt-num">{formatHoursMinutes(l.total_hours)}</span> },
    { key: "gross", label: "Gross", align: "right", width: "84px", render: (l) => <span className="dt-muted dt-nowrap">{(Number(l.total_seconds || 0) / 3600).toFixed(2)} hrs</span> },
    { key: "brk", label: "Break", align: "right", width: "84px", render: (l) => <span className="dt-muted dt-nowrap">{Math.round(Number(l.break_seconds || 0) / 60)} min</span> },
    { key: "net", label: "Net", align: "right", width: "84px", render: (l) => <span className="dt-muted dt-nowrap">{(Number(l.net_work_seconds || 0) / 3600).toFixed(2)} hrs</span> },
  ];
  const groupColumns = [
    { key: "name", label: "Project", render: ([name]) => <span className="dt-strong">{name}</span> },
    { key: "hrs", label: "Hours", align: "right", width: "140px", render: ([, hrs]) => <span className="dt-num">{formatHoursMinutes(hrs)}</span> },
  ];

  return (
    <CrewPilotModuleShell
      title="Hours Reports"
      subtitle="Report Builder"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      compact
    >
      <div className="rpt-split">
        <DeskPanel title="Configure Report">
          <div className="rpt-config">
            <CPDropdown label="Scope" value={reportScope} onChange={onScope} includePlaceholder={false} options={scopeOptions} />
            {reportScope === "crew_member" ? (
              <CPDropdown
                label="Employee"
                value={selectedEmployee}
                onChange={onEmployee}
                options={[
                  { value: "", label: "Select employee..." },
                  ...employees.map((e) => ({ value: e.supabase_user_id, label: e.employee_name })),
                ]}
              />
            ) : null}
            <CPDropdown
              label="Report Type"
              value={reportType}
              onChange={onReportType}
              options={[
                { value: "", label: "Select a report type…" },
                { value: "hours_by_project", label: "Hours By Project" },
                { value: "hours_by_date_range", label: "Hours By Date Range" },
              ]}
            />
            {isProject ? (
              <CPDropdown
                label="Project"
                value={project}
                onChange={onProject}
                options={[
                  { value: "", label: "Select a project…" },
                  ...projects.map((p) => ({ value: p.project_id, label: p.project_name })),
                ]}
              />
            ) : null}
            {isRange ? (
              <>
                <DateField label="Start Date" value={startDate} onChange={onStartDate} />
                <DateField label="End Date" value={endDate} onChange={onEndDate} />
              </>
            ) : null}
            <button type="button" className="dt-btn dt-btn--primary" disabled={!canGenerate || isGenerating} onClick={onGenerate}>
              {isGenerating ? "Generating…" : "Generate Report"}
            </button>
          </div>
        </DeskPanel>

        <div className="rpt-result">
          {reportResult ? (
            <>
              <div className="rpt-result__head">
                <h2 className="rpt-result__title">{reportResult.title}</h2>
                <span className="rpt-result__type">{reportResult.type}</span>
              </div>
              <DesktopKpiStrip
                ariaLabel="Report totals"
                tiles={[
                  { key: "hrs", value: formatHoursMinutes(reportResult.totalHours), label: "Total Project Hours", tone: "gold" },
                  { key: "ent", value: String(reportResult.entries), label: "Entries", tone: "blue" },
                ]}
              />
              {reportResult.grouped ? (
                <>
                  <h3 className="rpt-sec">Project Breakdown</h3>
                  <DeskTable columns={groupColumns} rows={Object.entries(reportResult.grouped)} rowKey={([name]) => name} caption="Project breakdown" />
                </>
              ) : null}
              {reportResult.logs ? (
                <>
                  <h3 className="rpt-sec">Detailed Entries</h3>
                  {reportResult.logs.length === 0 ? (
                    <div className="dt-card"><DeskEmpty title="No entries">No time logs match this report.</DeskEmpty></div>
                  ) : (
                    <DeskTable columns={logColumns} rows={reportResult.logs} rowKey={(l) => `${l.date_iso}-${l.employee}-${l.total_hours}-${l.start_time}`} caption="Detailed entries" />
                  )}
                </>
              ) : null}
            </>
          ) : (
            <div className="dt-card">
              <DeskEmpty title={reportType ? "Ready to generate" : "Select a report type"}>
                {reportType
                  ? "Complete the fields and choose Generate Report."
                  : "Select a report type to configure your report."}
              </DeskEmpty>
            </div>
          )}
        </div>
      </div>
    </CrewPilotModuleShell>
  );
}

/** Project analytics (desktop): filter bar, KPI strips, sortable-by-filter project table. */
export function ProjectAnalyticsDesktop({
  onBack,
  startDate,
  endDate,
  onStartDate,
  onEndDate,
  projectStatus,
  onProjectStatus,
  clientId,
  onClient,
  clients,
  projectId,
  onProject,
  projectOptions,
  sortKey,
  onSort,
  sortOptions,
  onGenerate,
  isGenerating,
  rows,
  grandTotals,
  onOpenProjectDetail,
}) {
  const columns = [
    {
      key: "project",
      label: "Project",
      primary: true,
      render: (r) => (
        <>
          <span className="dt-strong">{r.project_name}</span>
          {r.client_name ? <span className="dt-sub">{r.client_name}</span> : null}
        </>
      ),
    },
    { key: "hrs", label: "Hours", align: "right", width: "92px", render: (r) => <span className="dt-num">{formatHoursMinutes(r.totalHours)}</span> },
    { key: "lab", label: "Labour", align: "right", width: "96px", render: (r) => <span className="dt-num">{cash(r.labourCost)}</span> },
    { key: "mat", label: "Material", align: "right", width: "96px", render: (r) => <span className="dt-num">{cash(r.materialCost)}</span> },
    { key: "oth", label: "Other", align: "right", width: "86px", render: (r) => <span className="dt-num">{cash(r.otherCost)}</span> },
    { key: "act", label: "Actual", align: "right", width: "100px", render: (r) => <span className="dt-num" style={{ color: "var(--dt-gold)" }}>{cash(r.totalCost)}</span> },
    { key: "quo", label: "Quoted", align: "right", width: "100px", render: (r) => <span className="dt-num">{r.hasQuoteSnapshot ? cash(r.quoted || 0) : "—"}</span> },
    { key: "bil", label: "Billed", align: "right", width: "100px", render: (r) => <span className="dt-num">{cash(r.billed || 0)}</span> },
    { key: "mar", label: "Margin", align: "right", width: "100px", render: (r) => <span className={`dt-num ${Number(r.margin || 0) >= 0 ? "rpt-pos" : "rpt-neg"}`}>{cash(r.margin || 0)}</span> },
  ];

  return (
    <CrewPilotModuleShell
      title="Project Analytics"
      subtitle="Hours, labour cost, material cost, quoted, billed, and margin by project"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      compact
    >
      <DeskPanel title="Filters">
        <div className="rpt-filters">
          <div className="rpt-filters__field"><CPInput label="Start Date" type="date" value={startDate} onChange={onStartDate} /></div>
          <div className="rpt-filters__field"><CPInput label="End Date" type="date" value={endDate} onChange={onEndDate} /></div>
          <div className="rpt-filters__field">
            <CPDropdown
              label="Projects"
              value={projectStatus}
              onChange={onProjectStatus}
              includePlaceholder={false}
              options={[
                { value: "active", label: "Active Projects" },
                { value: "archived", label: "Archived Projects" },
                { value: "all", label: "All Projects" },
              ]}
            />
          </div>
          <div className="rpt-filters__field">
            <CPDropdown
              label="Client"
              value={clientId}
              onChange={onClient}
              options={[{ value: "", label: "All Clients" }, ...clients.map((c) => ({ value: c.client_id, label: c.client_name }))]}
            />
          </div>
          <div className="rpt-filters__field">
            <CPDropdown
              label="Project"
              value={projectId}
              onChange={onProject}
              options={[{ value: "", label: "All Projects" }, ...projectOptions.map((p) => ({ value: p.project_id, label: p.project_name }))]}
            />
          </div>
          <div className="rpt-filters__field">
            <CPDropdown label="Sort" value={sortKey} onChange={onSort} includePlaceholder={false} options={sortOptions} />
          </div>
          <button type="button" className="dt-btn dt-btn--primary rpt-filters__go" onClick={onGenerate} disabled={!startDate || !endDate || isGenerating}>
            {isGenerating ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </DeskPanel>

      <div style={{ height: 18 }} />

      {rows ? (
        <>
          <p className="rpt-range" style={{ margin: "0 0 12px" }}>
            Report period: <strong>{day(startDate)} → {day(endDate)}</strong>
          </p>
          <DesktopKpiStrip
            ariaLabel="Report summary"
            tiles={[
              { key: "proj", value: String(grandTotals.projectCount), label: "Projects", tone: "blue" },
              { key: "hrs", value: formatHoursMinutes(grandTotals.totalHours), label: "Total Hours", tone: "gold" },
              { key: "lab", value: cash(grandTotals.labourCost), label: "Labour Cost", tone: "gold" },
              { key: "mat", value: cash(grandTotals.materialCost), label: "Material Cost", tone: "gold" },
              { key: "oth", value: cash(grandTotals.otherCost), label: "Other Costs", tone: "gold" },
            ]}
          />
          <DesktopKpiStrip
            ariaLabel="Financial summary"
            tiles={[
              { key: "quo", value: cash(grandTotals.quoted), label: "Quoted (snapshot)", tone: "muted" },
              { key: "bil", value: cash(grandTotals.billed), label: "Billed (period)", tone: "blue" },
              { key: "cost", value: cash(grandTotals.totalCost), label: "Total Project Cost (period)", tone: "green" },
              { key: "mar", value: cash(grandTotals.margin), label: "Margin (Billed − Actual, period)", tone: grandTotals.margin >= 0 ? "green" : "red" },
            ]}
          />
          {rows.length === 0 ? (
            <div className="dt-card"><DeskEmpty title="No project activity">No project activity found for the selected filters.</DeskEmpty></div>
          ) : (
            <DeskTable
              columns={columns}
              rows={rows}
              rowKey={(r) => r.project_id}
              onOpen={
                onOpenProjectDetail
                  ? (r) => onOpenProjectDetail({ project_id: r.project_id, name: r.project_name, hrs: r.totalHours })
                  : undefined
              }
              openLabel={(r) => `View ${r.project_name} detail`}
              caption="Project analytics"
            />
          )}
        </>
      ) : (
        <div className="dt-card">
          <DeskEmpty title="No report yet">Set the filters and choose Generate Report.</DeskEmpty>
        </div>
      )}
    </CrewPilotModuleShell>
  );
}
